import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { COMMUNICATION_CATEGORIES } from './categories';

interface CategoryFilterDropdownProps {
    value: string | null;
    onChange: (value: string | null) => void;
}

// Bespoke single-value radio dropdown (the category filter takes exactly
// one value); chrome mirrors FilterDropdown, rows mirror CategoryAddDropdown.
export function CategoryFilterDropdown({ value, onChange }: CategoryFilterDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isActive = value !== null;

    const handlePick = (id: string) => {
        onChange(value === id ? null : id);
        setIsOpen(false);
    };

    return (
        <div className="relative inline-block text-left" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                style={{ minWidth: '160px' }}
                className={`inline-flex justify-between items-center rounded-full border-2 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors duration-300 w-full ${
                    isOpen ? 'border-gray-700' : 'border-gray-300'
                }`}
            >
                <span>Category</span>
                <span className="flex items-center ml-2">
                    <span
                        className={`mr-1 flex items-center justify-center h-5 min-w-5 px-1 rounded-full text-xs leading-none transition-opacity duration-200 ${
                            isActive ? 'bg-gray-700 text-white opacity-100' : 'opacity-0'
                        }`}
                    >
                        {isActive ? 1 : 0}
                    </span>
                    <ChevronDownIcon
                        className={`h-5 w-5 transition-transform duration-300 ${
                            isOpen ? 'transform rotate-180' : ''
                        }`}
                    />
                </span>
            </button>

            <div
                className={`absolute left-1/2 -translate-x-1/2 mt-2 w-56 z-20 bg-white shadow-lg rounded-2xl transition-all duration-300 ease-in-out overflow-hidden ring-2 ${
                    isOpen ? 'ring-gray-700' : 'ring-transparent'
                }`}
                style={{ maxHeight: isOpen ? '24rem' : '0', opacity: isOpen ? 1 : 0 }}
            >
                <div className="sticky top-0 bg-white px-3 pt-3 pb-2 flex items-center justify-between">
                    <span className="text-[10.5px] font-semibold tracking-wide text-gray-400 uppercase">
                        Category
                    </span>
                    {isActive && (
                        <button
                            type="button"
                            className="text-xs text-gray-500 underline hover:text-gray-700 transition-colors"
                            onClick={() => {
                                onChange(null);
                                setIsOpen(false);
                            }}
                        >
                            clear
                        </button>
                    )}
                </div>
                <div className="overflow-y-auto scrollbar-hide pb-1" style={{ maxHeight: '16rem' }}>
                    {COMMUNICATION_CATEGORIES.map((category) => {
                        const checked = value === category.id;
                        return (
                            <button
                                key={category.id}
                                type="button"
                                onClick={() => handlePick(category.id)}
                                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left hover:bg-gray-100"
                            >
                                <span
                                    className="w-1.5 h-1.5 rounded-full shrink-0"
                                    style={{ background: category.tone }}
                                />
                                <span className="min-w-0 flex-1 truncate text-gray-700 select-none">
                                    {category.id}
                                </span>
                                {checked && (
                                    <span className="h-1.5 w-1.5 rounded-full bg-gray-700 shrink-0" />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
